import { Controller } from "@hotwired/stimulus"

/**
 * Modern tab navigation controller (zero jQuery)
 * Handles accessible tab switching with proper ARIA attributes
 * Remembers last visited tab across page loads
 * Supports browser history with back/forward buttons
 */
export default class extends Controller {
  static targets = ["tab", "panel"]
  static values = {
    index: { type: Number, default: 0 },
    remember: { type: Boolean, default: true },
    updateUrl: { type: Boolean, default: true }
  }
  static classes = ["active", "inactive"]

  connect() {
    console.log('[tabs] Controller connected')
    console.log('[tabs] Found', this.tabTargets.length, 'tabs and', this.panelTargets.length, 'panels')
    console.log('[tabs] Remember:', this.rememberValue, 'Update URL:', this.updateUrlValue)
    console.log('[tabs] Initial indexValue:', this.indexValue)

    // Initialize ARIA attributes on first connection
    this.initializeAriaAttributes()

    // Check if this is a sync reload (takes priority)
    const syncReloadTab = sessionStorage.getItem('autosave_sync_reload_tab')
    if (syncReloadTab !== null) {
      const tabIndex = parseInt(syncReloadTab, 10)
      console.log('[tabs] 🔄 Sync reload detected! Restoring to tab:', tabIndex)
      sessionStorage.removeItem('autosave_sync_reload_tab') // Clear the flag
      this.indexValue = tabIndex
    }
    // Restore saved tab index if remember is enabled
    else if (this.rememberValue) {
      const savedIndex = this.getSavedIndex()
      console.log('[tabs] Saved index from sessionStorage:', savedIndex)
      console.log('[tabs] Storage key:', this.storageKey)
      if (savedIndex !== null) {
        console.log('[tabs] ✅ Restoring tab to index:', savedIndex)
        this.indexValue = savedIndex
      } else {
        console.log('[tabs] No saved index found, using default:', this.indexValue)
      }
    }

    // Initialize from URL parameter if present (only if updateUrl is enabled)
    if (this.updateUrlValue) {
      this.initializeFromUrl()
    }

    // Listen for popstate (browser back/forward)
    this.boundHandlePopState = this.handlePopState.bind(this)
    window.addEventListener('popstate', this.boundHandlePopState)

    // Show the current tab
    console.log('[tabs] Showing tab at index:', this.indexValue)
    this.showTab(this.indexValue)
  }

  disconnect() {
    console.log('[tabs] Controller disconnected')
    window.removeEventListener('popstate', this.boundHandlePopState)
    this.boundHandlePopState = null
  }

  /**
   * Handle tab selection click
   * @param {Event} event - Click event
   */
  async select(event) {
    event.preventDefault()
    console.log('╔═══════════════════════════════════════════╗')
    console.log('║         TAB SELECTION TRIGGERED           ║')
    console.log('╚═══════════════════════════════════════════╝')

    const clickedTab = event.currentTarget
    const newIndex = this.tabTargets.indexOf(clickedTab)

    console.log('[tabs] Clicked tab index:', newIndex)
    console.log('[tabs] Current tab index:', this.indexValue)
    console.log('[tabs] Tab targets available:', this.tabTargets.length)

    if (newIndex !== -1 && newIndex !== this.indexValue) {
      console.log('[tabs] ✓ Valid tab change detected')
      console.log('[tabs] Switching from tab', this.indexValue, 'to tab', newIndex)

      // Dispatch before-change event to allow saves before switching
      const beforeChangeEvent = new CustomEvent('tabs:before-change', {
        detail: {
          fromIndex: this.indexValue,
          toIndex: newIndex,
          fromPanel: this.panelTargets[this.indexValue],
          toPanel: this.panelTargets[newIndex]
        },
        bubbles: true,
        cancelable: false
      })

      console.log('[tabs] 📢 Dispatching tabs:before-change event...')
      console.log('[tabs] Event detail:', beforeChangeEvent.detail)
      this.element.dispatchEvent(beforeChangeEvent)
      console.log('[tabs] ✓ Event dispatched, waiting 100ms for async saves...')

      // Small delay to allow async saves to start
      await new Promise(resolve => setTimeout(resolve, 100))

      console.log('[tabs] ⏰ Delay complete, now switching tab...')
      // Now switch the tab
      this.indexValue = newIndex
      console.log('[tabs] ✅ Tab switch complete')
    } else {
      console.log('[tabs] ⏭️ No tab change needed (same tab or invalid index)')
    }
  }

  /**
   * Navigate to the next tab
   */
  next() {
    if (this.indexValue < this.tabTargets.length - 1) {
      this.indexValue++
    }
  }

  /**
   * Navigate to the previous tab
   */
  previous() {
    if (this.indexValue > 0) {
      this.indexValue--
    }
  }

  /**
   * Value changed callback - triggered when indexValue changes
   */
  indexValueChanged() {
    this.showTab(this.indexValue)

    // Save to session storage
    if (this.rememberValue) {
      this.saveIndex(this.indexValue)
    }

    // Update URL
    if (this.updateUrlValue) {
      this.updateUrlParameter(this.indexValue)
    }

    // Dispatch custom event for other controllers to listen
    this.dispatch('change', {
      detail: {
        index: this.indexValue,
        tab: this.tabTargets[this.indexValue],
        panel: this.panelTargets[this.indexValue]
      }
    })
  }

  /**
   * Initialize ARIA attributes for accessibility
   * Sets up proper ARIA roles and attributes without triggering jQuery UI
   */
  initializeAriaAttributes() {
    // Set role on tab buttons (after jQuery UI has loaded, so it won't auto-init)
    this.tabTargets.forEach((tab, i) => {
      // Don't set role="tab" - it triggers jQuery UI auto-initialization
      // Instead, use aria-selected and tabindex for keyboard nav
      tab.setAttribute('tabindex', i === this.indexValue ? '0' : '-1')
    })

    // Set aria-hidden on panels
    this.panelTargets.forEach((panel, i) => {
      panel.setAttribute('aria-hidden', i === this.indexValue ? 'false' : 'true')
    })

    console.log('[tabs] ARIA attributes initialized')
  }

  /**
   * Show the tab at the given index
   * @param {number} index - Tab index to show
   */
  showTab(index) {
    if (index < 0 || index >= this.tabTargets.length) {
      console.warn(`[tabs] Invalid index: ${index}`)
      return
    }

    // Verify we have matching tabs and panels
    if (this.tabTargets.length !== this.panelTargets.length) {
      console.error(`[tabs] Mismatch: ${this.tabTargets.length} tabs but ${this.panelTargets.length} panels`)
      return
    }

    this.tabTargets.forEach((tab, i) => {
      const panel = this.panelTargets[i]

      if (!panel) {
        console.error(`[tabs] No panel found for tab index ${i}`)
        return
      }

      const isActive = i === index

      if (isActive) {
        // Activate tab
        this.activateTab(tab)
        this.showPanel(panel)
      } else {
        // Deactivate tab
        this.deactivateTab(tab)
        this.hidePanel(panel)
      }
    })

    console.log(`[tabs] Switched to tab ${index}`)
  }

  /**
   * Activate a tab visually and semantically
   * @param {Element} tab - Tab element to activate
   */
  activateTab(tab) {
    if (this.hasActiveClass) {
      tab.classList.add(...this.activeClasses)
    }
    if (this.hasInactiveClass) {
      tab.classList.remove(...this.inactiveClasses)
    }
    tab.setAttribute('aria-selected', 'true')
    tab.setAttribute('tabindex', '0')
  }

  /**
   * Deactivate a tab visually and semantically
   * @param {Element} tab - Tab element to deactivate
   */
  deactivateTab(tab) {
    if (this.hasActiveClass) {
      tab.classList.remove(...this.activeClasses)
    }
    if (this.hasInactiveClass) {
      tab.classList.add(...this.inactiveClasses)
    }
    tab.setAttribute('aria-selected', 'false')
    tab.setAttribute('tabindex', '-1')
  }

  /**
   * Show a panel
   * @param {Element} panel - Panel element to show
   */
  showPanel(panel) {
    panel.classList.remove('hidden')
    panel.removeAttribute('hidden')
    panel.setAttribute('aria-hidden', 'false')
  }

  /**
   * Hide a panel
   * @param {Element} panel - Panel element to hide
   */
  hidePanel(panel) {
    panel.classList.add('hidden')
    panel.setAttribute('hidden', '')
    panel.setAttribute('aria-hidden', 'true')
  }

  /**
   * Initialize tab from URL parameter
   */
  initializeFromUrl() {
    const urlParams = new URLSearchParams(window.location.search)
    const tabParam = urlParams.get('tab')

    if (tabParam !== null) {
      const tabIndex = parseInt(tabParam, 10)
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex < this.tabTargets.length) {
        this.indexValue = tabIndex
      }
    }
  }

  /**
   * Update URL parameter without page reload
   * @param {number} index - Current tab index
   */
  updateUrlParameter(index) {
    if (!this.updateUrlValue) {
      console.log('[tabs] URL updates disabled, skipping')
      return
    }

    const url = new URL(window.location)
    url.searchParams.set('tab', index)

    // Use pushState to update URL without page reload
    window.history.pushState(
      { tabIndex: index },
      '',
      url.toString()
    )

    console.log('[tabs] Updated URL to', url.toString())
  }

  /**
   * Handle browser back/forward button
   * @param {PopStateEvent} event - Popstate event
   */
  handlePopState(event) {
    if (event.state && typeof event.state.tabIndex === 'number') {
      // Restore tab from history state
      this.indexValue = event.state.tabIndex
    } else {
      // Fallback to URL parameter
      this.initializeFromUrl()
    }
  }

  /**
   * Get storage key for session storage
   * @returns {string} Storage key
   */
  get storageKey() {
    return `tabs-${this.element.id || 'default'}-index`
  }

  /**
   * Save tab index to session storage
   * @param {number} index - Tab index to save
   */
  saveIndex(index) {
    try {
      sessionStorage.setItem(this.storageKey, index.toString())
    } catch (error) {
      console.warn('[tabs] Failed to save index to sessionStorage:', error)
    }
  }

  /**
   * Get saved tab index from session storage
   * @returns {number|null} Saved index or null
   */
  getSavedIndex() {
    try {
      const saved = sessionStorage.getItem(this.storageKey)
      if (saved !== null) {
        const index = parseInt(saved, 10)
        if (!isNaN(index)) {
          return index
        }
      }
    } catch (error) {
      console.warn('[tabs] Failed to retrieve index from sessionStorage:', error)
    }
    return null
  }

  /**
   * Get the currently active tab element
   * @returns {Element} Current tab element
   */
  get currentTab() {
    return this.tabTargets[this.indexValue]
  }

  /**
   * Get the currently active panel element
   * @returns {Element} Current panel element
   */
  get currentPanel() {
    return this.panelTargets[this.indexValue]
  }

  /**
   * Check if on first tab
   * @returns {boolean} True if on first tab
   */
  get isFirstTab() {
    return this.indexValue === 0
  }

  /**
   * Check if on last tab
   * @returns {boolean} True if on last tab
   */
  get isLastTab() {
    return this.indexValue === this.tabTargets.length - 1
  }
}
